import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'

export const Route = createFileRoute('/unsubscribe')({
  validateSearch: (s: Record<string, unknown>) => ({ token: (s.token as string) ?? '' }),
  component: UnsubscribePage,
})

function UnsubscribePage() {
  const { token } = Route.useSearch()
  const [state, setState] = useState<'checking' | 'valid' | 'already' | 'invalid' | 'done' | 'error'>('checking')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!token) return setState('invalid')
    fetch(`/email/unsubscribe?token=${encodeURIComponent(token)}`)
      .then((r) => r.json())
      .then((d) => {
        if (d?.valid) setState('valid')
        else if (d?.reason === 'already_unsubscribed') setState('already')
        else setState('invalid')
      })
      .catch(() => setState('error'))
  }, [token])

  const confirm = async () => {
    setBusy(true)
    try {
      const r = await fetch('/email/unsubscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })
      const d = await r.json()
      if (d?.success) setState('done')
      else if (d?.reason === 'already_unsubscribed') setState('already')
      else setState('error')
    } catch {
      setState('error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6">
      <div className="max-w-md w-full rounded-2xl border border-border bg-card/40 p-8 text-center space-y-4">
        <h1 className="font-display text-2xl font-bold text-gradient-gold">Unsubscribe</h1>
        {state === 'checking' && <p className="text-muted-foreground">Checking your link...</p>}
        {state === 'valid' && (
          <>
            <p className="text-muted-foreground">Click below to stop receiving emails from XAU Pro Academy.</p>
            <Button variant="gold" onClick={confirm} disabled={busy} className="w-full">
              {busy ? 'Working...' : 'Confirm Unsubscribe'}
            </Button>
          </>
        )}
        {state === 'already' && <p className="text-muted-foreground">You are already unsubscribed. Nothing more to do.</p>}
        {state === 'done' && <p className="text-muted-foreground">You have been unsubscribed. Sorry to see you go.</p>}
        {state === 'invalid' && <p className="text-muted-foreground">This link is invalid or has expired.</p>}
        {state === 'error' && <p className="text-destructive">Something went wrong. Please email support@xauproacademy.com.</p>}
      </div>
    </div>
  )
}
